/**
 * ErrorOverlay - displays global app logs in bottom-right corner
 *
 * Shows logs from non-node sources (node logs have their own per-node display).
 * Logs are expandable to show stack traces and additional details.
 *
 * Subscribes directly to logger to avoid re-rendering the entire app on every log.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { logger, LogLevel, LogEntry } from '@/logging';
import { LogList, LEVEL_COLORS } from './LogList';

const LEVEL_ORDER: LogLevel[] = [LogLevel.Error, LogLevel.Info, LogLevel.Trace];

export function ErrorOverlay() {
  const [logs, setLogs] = useState<LogEntry[]>(() => logger.getAll());
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [enabledLevels, setEnabledLevels] = useState<Set<LogLevel>>(
    new Set([LogLevel.Error])
  );
  const [isExpanded, setIsExpanded] = useState(false);

  // Subscribe directly to logger (only this component re-renders on log changes)
  useEffect(() => {
    const unsubscribe = logger.subscribe(() => {
      setLogs(logger.getAll());
    });
    return unsubscribe;
  }, []);

  // Filter out node logs (have context) for error indicator
  const globalEntries = useMemo(() =>
    logs.filter(e => !e.context),
    [logs]
  );

  const hasErrors = useMemo(() =>
    globalEntries.some(e => e.level === LogLevel.Error),
    [globalEntries]
  );

  const togglePanel = useCallback(() => setIsExpanded(prev => !prev), []);

  const toggleLevel = useCallback((level: LogLevel) => {
    setEnabledLevels(prev => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  }, []);

  // Get unique sources (excluding node logs)
  const sources = useMemo(() =>
    [...new Set(globalEntries.map(e => e.source))],
    [globalEntries]
  );

  // Filter by level and source
  const filteredEntries = useMemo(() => {
    return globalEntries.filter(e => {
      if (!enabledLevels.has(e.level)) return false;
      if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
      return true;
    });
  }, [globalEntries, sourceFilter, enabledLevels]);

  const handleClear = useCallback(() => {
    if (sourceFilter === 'all') {
      logger.clearGlobal();
    } else {
      logger.clearBySource(sourceFilter);
    }
    setLogs(logger.getAll());
  }, [sourceFilter]);

  const handleCopy = useCallback(() => {
    const text = filteredEntries.slice(-10).map(entry => {
      let line = `[${entry.level}] [${entry.source}] ${entry.message}`;
      if (entry.stack) {
        line += `\n${entry.stack}`;
      }
      return line;
    }).join('\n\n');
    navigator.clipboard.writeText(text);
  }, [filteredEntries]);

  // Always show toggle button
  if (!isExpanded) {
    return (
      <button
        onClick={togglePanel}
        style={{
          ...styles.toggleButton,
          background: hasErrors ? '#dc2626' : '#374151'
        }}
      />
    );
  }

  return (
    <div style={styles.overlay}>
      <LogList entries={filteredEntries} maxItems={15} />

      <div style={styles.toolbar}>
        <div style={styles.levelButtons}>
          {LEVEL_ORDER.map(level => (
            <button
              key={level}
              onClick={() => toggleLevel(level)}
              style={{
                ...styles.levelButton,
                background: enabledLevels.has(level) ? LEVEL_COLORS[level] : '#333',
                color: enabledLevels.has(level) ? '#fff' : '#666'
              }}
            >
              {level}
            </button>
          ))}
        </div>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={styles.select}
        >
          <option value="all">All Sources</option>
          {sources.map(src => (
            <option key={src} value={src}>{src}</option>
          ))}
        </select>

        <span style={styles.count}>{filteredEntries.length}/{globalEntries.length}</span>
        <button onClick={handleCopy} style={styles.copyButton} title="Copy logs">Copy</button>
        <button onClick={handleClear} style={styles.clearButton}>Clear</button>
        <button onClick={togglePanel} style={styles.collapseButton}>✕</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    width: 480,
    maxHeight: 400,
    background: 'rgba(30, 30, 30, 0.98)',
    border: '1px solid #444',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 1000,
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column' as const
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#1f1f1f',
    borderTop: '1px solid #444',
    flexShrink: 0
  },
  count: {
    fontSize: 11,
    color: '#9ca3af',
    marginLeft: 'auto'
  },
  levelButtons: {
    display: 'flex',
    gap: 2
  },
  levelButton: {
    border: 'none',
    borderRadius: 3,
    padding: '4px 8px',
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  select: {
    background: '#333',
    color: '#fff',
    border: '1px solid #555',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 11,
    cursor: 'pointer'
  },
  copyButton: {
    background: '#374151',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 11,
    cursor: 'pointer'
  },
  clearButton: {
    background: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 11,
    cursor: 'pointer'
  },
  toggleButton: {
    position: 'fixed' as const,
    bottom: 20,
    right: 20,
    width: 15,
    height: 15,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    zIndex: 1000
  },
  collapseButton: {
    background: 'transparent',
    color: '#9ca3af',
    border: 'none',
    fontSize: 14,
    cursor: 'pointer',
    padding: '4px 8px',
    marginLeft: 4
  }
};
